import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { nextPow2 } from "../../utils/bracketLogic";
import { useApi } from "../../contexts/ApiContext";
import { useFeatures } from "../../contexts/FeatureContext";
import "./SetupScreen.css";

function SetupScreen({ onStart }) {
  const { playersApi, tournamentsApi } = useApi();
  const { flags } = useFeatures();
  const ratingsOn = flags.ratingsEnabled;
  const queryClient = useQueryClient();

  const [tournamentSearch, setTournamentSearch] = useState("");
  const [tournamentDropdownOpen, setTournamentOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);

  const [playerSearch, setPlayerSearch] = useState("");
  const [playerDropdownOpen, setPlayerDropdownOpen] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState([]);

  const [format, setFormat] = useState("single"); // "single" | "double"

  const [error, setError] = useState("");

  const tournamentRef = useRef(null);
  const playerRef = useRef(null);

  const { data: allPlayers = [] } = useQuery({
    queryKey: ["players"],
    queryFn: () => playersApi.getAll(),
  });

  const { data: allTournamentsRaw = [] } = useQuery({
    queryKey: ["tournaments"],
    queryFn: () => tournamentsApi.getAll(),
  });
  const allTournaments = allTournamentsRaw.filter((t) => t.status === 0);

  useEffect(() => {
    function handleClickOutside(e) {
      if (tournamentRef.current && !tournamentRef.current.contains(e.target))
        setTournamentOpen(false);
      if (playerRef.current && !playerRef.current.contains(e.target))
        setPlayerDropdownOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredTournaments = allTournaments
    .filter(
      (t) =>
        tournamentSearch === "" ||
        t.name.toLowerCase().startsWith(tournamentSearch.toLowerCase()),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, 10);

  const selectedPlayerIds = selectedPlayers
    .filter((p) => p.id)
    .map((p) => p.id);

  const filteredPlayers = allPlayers
    .filter((p) => !selectedPlayerIds.includes(p.id))
    .filter(
      (p) =>
        playerSearch === "" ||
        p.name.toLowerCase().startsWith(playerSearch.toLowerCase()),
    )
    .sort((a, b) => a.name.localeCompare(b.name))
    .slice(0, 10);

  function selectTournament(t) {
    setSelectedTournament(t);
    setTournamentSearch(t.name);
    setTournamentOpen(false);
  }

  function selectPlayer(p) {
    setSelectedPlayers((prev) => [
      ...prev,
      { id: p.id, name: p.name, elo: p.elo ?? p.Elo ?? null, isNew: false },
    ]);
    setPlayerSearch("");
    setPlayerDropdownOpen(false);
    setError("");
  }

  function addNewPlayer(name) {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (
      selectedPlayers.some(
        (p) => p.name.toLowerCase() === trimmed.toLowerCase(),
      )
    )
      return setError("Player already added.");
    setSelectedPlayers((prev) => [
      ...prev,
      { id: null, name: trimmed, elo: null, isNew: true },
    ]);
    setPlayerSearch("");
    setPlayerDropdownOpen(false);
    setError("");
  }

  function handleAddAll() {
    const alreadySelected = new Set(selectedPlayerIds);
    const toAdd = allPlayers
      .filter((p) => !alreadySelected.has(p.id))
      .map((p) => ({
        id: p.id,
        name: p.name,
        elo: p.elo ?? p.Elo ?? null,
        isNew: false,
      }));
    if (toAdd.length === 0) return setError("All players are already added.");
    setSelectedPlayers((prev) => [...prev, ...toAdd]);
    setError("");
  }

  function removePlayer(index) {
    setSelectedPlayers((prev) => prev.filter((_, i) => i !== index));
  }

  const generateMutation = useMutation({
    mutationFn: async () => {
      const resolvedPlayers = await Promise.all(
        selectedPlayers.map(async (p) => {
          if (p.isNew) {
            const created = await playersApi.create({ name: p.name });
            return { ...created, elo: created.elo ?? created.Elo ?? null };
          }
          return p;
        }),
      );

      let tournament = selectedTournament
        ? selectedTournament
        : await tournamentsApi.create({ name: tournamentSearch.trim() });

      const updated = await tournamentsApi.update(tournament.id, {
        ...tournament,
        playerIds: resolvedPlayers.map((p) => p.id),
        status: 1,
      });

      return { resolvedPlayers, tournament, updated };
    },
    onSuccess: ({ resolvedPlayers, tournament, updated }) => {
      queryClient.invalidateQueries({ queryKey: ["players"] });
      queryClient.invalidateQueries({ queryKey: ["tournaments"] });

      const ranked = [...resolvedPlayers]
        .sort((a, b) => {
          const aElo = a.elo ?? a.Elo ?? null;
          const bElo = b.elo ?? b.Elo ?? null;
          if (aElo == null && bElo == null) return 0;
          if (aElo == null) return 1;
          if (bElo == null) return -1;
          return bElo - aElo;
        })
        .map((p) => ({ name: p.name, elo: p.elo ?? p.Elo ?? null }));

      onStart(
        resolvedPlayers.map((p) => p.name),
        updated ?? { ...tournament, status: 1 },
        ranked,
        format,
      );
    },
    onError: (e) =>
      setError(e.message || "Something went wrong. Is the API running?"),
  });

  function handleGenerate() {
    if (!tournamentSearch.trim())
      return setError("Please enter a tournament name.");
    if (selectedPlayers.length < 2)
      return setError("Please add at least 2 players.");
    setError("");
    generateMutation.mutate();
  }

  const loading = generateMutation.isPending;

  const size =
    selectedPlayers.length >= 2 ? nextPow2(selectedPlayers.length) : null;
  const isExactPlayerMatch = filteredPlayers.some(
    (p) => p.name.toLowerCase() === playerSearch.toLowerCase(),
  );
  const availableCount = allPlayers.filter(
    (p) => !selectedPlayerIds.includes(p.id),
  ).length;

  return (
    <div className="tn-setup">
      <div className="crud-header">
        <h1 className="crud-title">Create Tournament</h1>
        <p className="crud-sub">
          Search or create a tournament, then add your players.
        </p>
      </div>

      <div className="tn-form-card">
        {/* ── Tournament search ── */}
        <div className="tn-search-group" ref={tournamentRef}>
          <label className="tn-search-label">Tournament</label>
          <div className="tn-dropdown-wrap">
            <input
              className="tn-input"
              placeholder="Search or create a tournament..."
              value={tournamentSearch}
              onChange={(e) => {
                setTournamentSearch(e.target.value);
                setSelectedTournament(null);
                setTournamentOpen(true);
              }}
              onFocus={() => setTournamentOpen(true)}
            />
            {tournamentDropdownOpen && filteredTournaments.length > 0 && (
              <div className="tn-dropdown">
                {filteredTournaments.map((t) => (
                  <div
                    key={t.id}
                    className="tn-dropdown-item"
                    onMouseDown={() => selectTournament(t)}
                  >
                    {t.name}
                  </div>
                ))}
              </div>
            )}
          </div>
          {tournamentSearch.trim() && !selectedTournament && (
            <p className="tn-new-hint">
              ✦ "{tournamentSearch}" will be created as a new tournament
            </p>
          )}
        </div>

        {/* ── Format ── */}
        <div className="tn-search-group">
          <label className="tn-search-label">Format</label>
          <div className="tn-format-toggle">
            <button
              type="button"
              className={`tn-format-opt${format === "single" ? " active" : ""}`}
              onClick={() => setFormat("single")}
            >
              Single Elimination
            </button>
            <button
              type="button"
              className={`tn-format-opt${format === "double" ? " active" : ""}`}
              onClick={() => setFormat("double")}
            >
              Double Elimination
            </button>
          </div>
        </div>

        {/* ── Player search ── */}
        <div className="tn-search-group" ref={playerRef}>
          <label className="tn-search-label">Players</label>
          <div className="tn-dropdown-wrap">
            <input
              className="tn-input"
              placeholder="Search or add a player..."
              value={playerSearch}
              onChange={(e) => {
                setPlayerSearch(e.target.value);
                setPlayerDropdownOpen(true);
              }}
              onFocus={() => setPlayerDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && playerSearch.trim())
                  addNewPlayer(playerSearch);
              }}
            />
            {playerDropdownOpen &&
              (filteredPlayers.length > 0 ||
                playerSearch.trim() ||
                availableCount > 0) && (
                <div className="tn-dropdown">
                  {availableCount > 0 && (
                    <div
                      className="tn-dropdown-item tn-dropdown-addall"
                      onMouseDown={handleAddAll}
                    >
                      ＋ Add all players ({availableCount})
                    </div>
                  )}
                  {filteredPlayers.map((p) => (
                    <div
                      key={p.id}
                      className="tn-dropdown-item"
                      onMouseDown={() => selectPlayer(p)}
                    >
                      {p.name}
                    </div>
                  ))}
                  {playerSearch.trim() && !isExactPlayerMatch && (
                    <div
                      className="tn-dropdown-item tn-dropdown-new"
                      onMouseDown={() => addNewPlayer(playerSearch)}
                    >
                      ✦ Add "{playerSearch}" as new player
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>

        {/* ── Selected players list ── */}
        {selectedPlayers.length > 0 && (
          <div className="tn-list">
            {selectedPlayers.map((p, i) => (
              <div key={i} className="tn-item">
                <span className="tn-num">{i + 1}</span>
                <span className="tn-name">{p.name}</span>
                {ratingsOn && p.elo != null && (
                  <span className="tn-elo-badge">{p.elo}</span>
                )}
                {p.isNew && <span className="tn-new-badge">new</span>}
                <button className="tn-del" onClick={() => removePlayer(i)}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <p className="tn-err">{error}</p>

        <div className="tn-meta">
          <span>
            {selectedPlayers.length} player
            {selectedPlayers.length !== 1 ? "s" : ""} added
          </span>
          {size && <span>Bracket size: {size}</span>}
        </div>

        <button
          className="tn-gen"
          disabled={
            selectedPlayers.length < 2 || !tournamentSearch.trim() || loading
          }
          onClick={handleGenerate}
        >
          {loading ? "CREATING..." : "GENERATE BRACKET"}
        </button>
      </div>
    </div>
  );
}

export default SetupScreen;
